import React, {ChangeEvent, useCallback, useState} from 'react';
import {Product} from 'components/customer/Product/entity/Product';
import {Button, Spinner} from 'react-bootstrap';
import {useDialogContext, useNotificationContext} from 'contexts';
import styled from 'styled-components';
import {useJobCabinetFieldUpdate} from 'components/customer/Product/helpers/useJobCabinetFieldUpdate';
import {genericMessageHandler} from 'shared/helpers';
import {useUpdateBenchMutation} from 'components/customer/BTM/store/btmApi';

interface VariationRequestFormProps {
    product: Partial<Product>;
    roomId: number;
}

export const VariationRequestForm = ({
    product,
    roomId,
}: VariationRequestFormProps) => {
    const {notify} = useNotificationContext();
    const {hideDialog} = useDialogContext();
    const [comment, setComment] = useState(product.comment);

    const {isLoading, updateFields} = useJobCabinetFieldUpdate(roomId);
    const [updateBTMQuantity, {isLoading: isLoadingBTMComment}] =
        useUpdateBenchMutation();

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLTextAreaElement>) => {
            const updatedComment = event.target.value;
            setComment(updatedComment);
        },
        []
    );

    const saveProduct = useCallback(async () => {
        try {
            if (product.hasOwnProperty('job_cabinet_id')) {
                await updateFields({
                    job_cabinet_id: product.job_cabinet_id,
                    comment,
                });
            } else {
                await updateBTMQuantity({
                    bench: {comment},
                    id: Number(product.id),
                });
            }

            genericMessageHandler(
                notify,
                {
                    message: 'Variation request updated successfully',
                },
                'success'
            );
            hideDialog();
        } catch (error) {
            if (error instanceof Error) {
                genericMessageHandler(notify, {
                    message: error.message,
                });
            }
        }
    }, [product, comment, updateFields, updateBTMQuantity, notify, hideDialog]);

    const saveComment = useCallback(() => {
        void saveProduct();
    }, [saveProduct]);

    return (
        <div>
            <Textarea rows={10} onChange={handleChange} value={comment} />

            <FauxFooter>
                <Button
                    variant="warning"
                    disabled={isLoading || isLoadingBTMComment}
                    onClick={hideDialog}>
                    Close
                </Button>

                <Button
                    variant="primary"
                    disabled={isLoading || isLoadingBTMComment}
                    onClick={saveComment}>
                    {isLoading || isLoadingBTMComment ? (
                        <CustomSpinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </CustomSpinner>
                    ) : (
                        'Save'
                    )}
                </Button>
            </FauxFooter>
        </div>
    );
};

const Textarea = styled.textarea`
    width: 100%;
`;

const CustomSpinner = styled(Spinner)`
    width: 15px;
    height: 15px;
    border-width: 3px;
`;

const FauxFooter = styled.div`
    border-top: 1px solid #dee2e6;
    padding: 15px 15px 0;
    margin: 15px -15px 0;

    display: flex;
    justify-content: space-between;

    > button {
        border-radius: 10px;
        padding: 5px 10px;
        border: 0;
        font-size: 0.95em;
        font-weight: 500;
    }
`;
